import React, { useMemo } from 'react'
import { Form } from 'antd'
import { useStore, useUnit } from 'effector-react'
import dayjs, { Dayjs } from 'dayjs'
import {
  $otherPaymentForm,
  addOtherPayment,
  addOtherPaymentMounted,
  deleteOtherPayment,
  deleteOtherPaymentMounted,
  editOtherPayment,
  editOtherPaymentMounted,
  getOtherPaymentById,
  getOtherPaymentByIdMounted,
  setOtherPaymentForm,
} from './model'
import { $userProfile } from '../../../../../features/user'
import { transactionType } from '../../../../../libs/finSystem'
import {
  formatTransactionDateTime,
  parseNaiveDateTime,
} from '../../../../../libs/utils'
import { $wallet as $currentWallet } from '../../details/model'
import { initialOtherModalState } from './const'
import { ModalType } from '../types'
import { $walletAllowedList } from '../GeneralTransactionModal/model'
import { TransactionModalLayout } from '../TransactionModalLayout'
import { useTransactionForm } from '../useTransactionForm'
import {
  AmountSentReceivedRow,
  CommentField,
  CommissionRow,
  DateTimeRow,
  ExchangeRateRow,
  LockedTypeSelect,
  ProductSelect,
  TextField,
  WalletSelect,
  useDefaultCurrencyOnce,
} from '../transactionFields'

type Props = {
  isOpen: boolean
  onClose: () => void
  onCancel?: () => void
  mode?: 'create' | 'edit'
  transactionId?: string
  type: ModalType
}

type FormValues = {
  walletId: string
  toWallet: string
  product: string
  sentAmount: string
  sentCurrency: string
  receivedAmount: string
  receivedCurrency: string
  sentCommission: string
  sentCommissionCurrency: string
  receivedCommission: string
  receivedCommissionCurrency: string
  sentExchangeRate: string
  receivedExchangeRate: string
  date: Dayjs | null
  time: Dayjs | null
  inProgress: boolean
  comment: string
}

export const OtherTransactionModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onCancel,
  transactionId,
  mode = 'create',
  type,
}) => {
  const [form] = Form.useForm<FormValues>()

  const userProfile = useStore($userProfile)
  const allWallets = useUnit($walletAllowedList)
  const currentWallet = useStore($currentWallet)

  const walletId = Form.useWatch('walletId', form)

  useDefaultCurrencyOnce(form, 'sentCurrency', 'sentCommissionCurrency')
  useDefaultCurrencyOnce(form, 'receivedCurrency', 'receivedCommissionCurrency')

  const fromWalletCurrencies = useMemo(() => {
    if (currentWallet.id) {
      return currentWallet.localBalances?.map((item) => item.currency) ?? []
    }
    const w = allWallets.find((item) => Number(item.id) === Number(walletId))
    return w?.currencies ?? []
  }, [allWallets, walletId, currentWallet])

  const {
    storeData: otherPaymentForm,
    handleSubmit,
    handleSubmitAndCreateNew,
    handleCancel,
    handleDelete,
    isSubmitting,
    isDeleting,
    isLoadingForm,
  } = useTransactionForm({
    form,
    mode,
    transactionId,
    isOpen,
    onClose,
    onCancel,
    store: $otherPaymentForm,
    initialState: initialOtherModalState,
    setForm: setOtherPaymentForm,
    mapStoreToForm: (data) => {
      const dt = parseNaiveDateTime(data.dateTime)
      return {
        walletId: String(data.walletId ?? ''),
        toWallet: data.toWallet,
        product: data.product,
        sentAmount: String(data.sentAmount ?? ''),
        sentCurrency: data.sentCurrency,
        receivedAmount: String(data.receivedAmount ?? ''),
        receivedCurrency: data.receivedCurrency,
        sentCommission: String(data.sentCommission ?? '0'),
        sentCommissionCurrency: data.sentCommissionCurrency,
        receivedCommission: String(data.receivedCommission ?? '0'),
        receivedCommissionCurrency: data.receivedCommissionCurrency,
        sentExchangeRate: String(data.sentExchangeRate ?? '0'),
        receivedExchangeRate: String(data.receivedExchangeRate ?? '0'),
        date: dt ? dayjs(dt.date) : dayjs(),
        time: dt ? dayjs(dt.time, 'HH:mm:ss') : dayjs(),
        inProgress: data.inProgress,
        comment: data.comment,
      }
    },
    events: {
      addMount: addOtherPaymentMounted,
      editMount: editOtherPaymentMounted,
      deleteMount: deleteOtherPaymentMounted,
      getByIdMount: getOtherPaymentByIdMounted,
    },
    effects: {
      add: addOtherPayment,
      edit: editOtherPayment,
      delete: deleteOtherPayment,
      getById: getOtherPaymentById,
    },
    historyFields: { toWallet: 'finSystem.recipient' },
    buildPayload: (values, { mode: m, transactionId: tid }) => {
      const wallet =
        values.walletId || (currentWallet.id ? String(currentWallet.id) : '')
      return {
        ...values,
        id: m === 'edit' ? tid : '',
        wallet,
        walletId: wallet,
        sentCommission: values.sentCommission || '0',
        receivedCommission: values.receivedCommission || '0',
        sentExchangeRate: values.sentExchangeRate || '0',
        receivedExchangeRate: values.receivedExchangeRate || '0',
        psCommission: '',
        inProgress: !!values.inProgress,
        dateTime: formatTransactionDateTime(
          values.date!.format('YYYY-MM-DD'),
          values.time!.format('HH:mm:ss'),
        ),
      } as any
    },
  })

  return (
    <TransactionModalLayout
      isOpen={isOpen}
      onClose={handleCancel}
      title={`${mode === 'edit' ? 'Edit' : 'Create'} payment${otherPaymentForm.id ? ` #${otherPaymentForm.id}` : ''}`}
      mode={mode}
      creatorEmail={userProfile.email}
      isLoading={isLoadingForm}
      isSubmitting={isSubmitting}
      isDeleting={isDeleting}
      onSubmit={handleSubmit}
      onSubmitAndCreateNew={
        mode === 'create' ? handleSubmitAndCreateNew : undefined
      }
      onDelete={mode === 'edit' ? handleDelete : undefined}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          walletId:
            type === 'wallet' && currentWallet.id
              ? String(currentWallet.id)
              : '',
          sentCommission: '0',
          receivedCommission: '0',
          sentExchangeRate: '0',
          receivedExchangeRate: '0',
          inProgress: false,
          date: dayjs(),
          time: dayjs(),
        }}
        requiredMark={false}
      >
        <div style={{ display: 'flex', gap: 8 }}>
          <WalletSelect
            name="walletId"
            label="From"
            required
            disabled={type === 'wallet' || mode === 'edit'}
            style={{ flex: 1 }}
          />
          <LockedTypeSelect
            value={transactionType[4].value}
            options={transactionType}
            style={{ flex: 1 }}
          />
        </div>

        <TextField
          name="toWallet"
          label="To"
          placeholder="Recipient"
          required
          historyScope="finSystem.recipient"
        />

        <ProductSelect name="product" />

        <AmountSentReceivedRow
          sentAmountName="sentAmount"
          sentCurrencyName="sentCurrency"
          sentCurrencies={fromWalletCurrencies}
          sentRequired
          sentAllowNegative
          sentCurrencyRequired
          receivedAmountName="receivedAmount"
          receivedCurrencyName="receivedCurrency"
          receivedCurrencies={fromWalletCurrencies}
          receivedAllowNegative
          receivedCurrencyAllowClear
        />

        <CommissionRow
          sentName="sentCommission"
          sentCurrencyName="sentCommissionCurrency"
          sentCurrencies={fromWalletCurrencies}
          receivedName="receivedCommission"
          receivedCurrencyName="receivedCommissionCurrency"
          receivedCurrencies={fromWalletCurrencies}
        />

        <ExchangeRateRow
          sentName="sentExchangeRate"
          receivedName="receivedExchangeRate"
        />

        <DateTimeRow inProgressName="inProgress" />

        <CommentField />
      </Form>
    </TransactionModalLayout>
  )
}
